import { WbsItemStatus } from '../../../domain/entity/WbsItemEntity'
import {
  demoWbsItemBodies,
  getDemoWbsItemParentNodeByUuid,
  REQUIREMENTS_PHASE_ITEMS,
  DEMO_PLAN_START_DAY,
} from './wbs-items'
import { demoTeams } from './members'
import { countDemoThreadsByTemplatePage } from './discussion'
import { demoUser } from '../tenants/tenant'
import {
  dayOffsetToDate,
  dayOffsetToEpochMs,
  TODAY_DAY_OFFSET,
} from '../shared'

// 進捗レポート（view/pages/ProjectVersionReport）のデモデータ。
// この画面はテンプレート > バージョン（レポート世代） > ページ（スライド）
// という構造を持ち、ページごとの数値はスナップショットAPIから別途取得して
// targetUuidで突き合わせる設計。ここではその両方を、既存のデモWBS
// （wbs-items.ts）から導出して組み立てる。
//
// 数値をWBSから導出するのは、グリッドに出る進捗率・遅延件数が
// プロジェクト計画画面・ガント画面と食い違わないようにするため。
// レポート用に独立した数字を置くと、同じデモプロジェクトなのに画面ごとに
// 進捗が違う、という不整合が見えてしまう。

const TEMPLATE_UUID = 'demo-version-report-template'
const TEMPLATE_NAME = '週次進捗報告'

// レポートは週次で3世代。直近を最新とし、過去2世代は1週間ずつ遡る。
// 「いつ開いても直近3週分が溜まっている」状態にするため、固定日付ではなく
// デモ日付軸（TODAY_DAY_OFFSET）からの相対で決める。
const REPORT_GENERATION_COUNT = 3
const DAYS_PER_WEEK = 7

const SNAPSHOT_ROOT_UUID = 'demo-wg-1'

// 工程別ページの対象。depth 0 の工程がそのままレポートの行になる。
// WBS項目のuuidはcode（wbs-items.tsのaddWbsItem参照）なので、
// targetUuidにはcodeを入れる。
const DEMO_ROOT_PROCESS_CODES = REQUIREMENTS_PHASE_ITEMS.filter(
  item => item.depth === 0
).map(item => item.code)

// 集計範囲（rootUuid）に選べるWBSの種別。ワークグループ・工程・成果物一覧
// までで、その配下の成果物・タスクは集計される側なので範囲にはならない。
// スナップショットはワークグループもPROCESSとして返す（本体の
// resolveSnapshotWbsItemTypeはpath.length === 1かどうかで見分ける）ため、
// ここでワークグループを別扱いする必要はない。
const SELECTABLE_SCOPE_TYPES = ['PROCESS', 'DELIVERABLE_LIST']

// 進捗概況ページ（WBSツリー＋ガント）の対象。
// ツリーに出るのは成果物一覧より上の階層（ワークグループ・工程・成果物一覧）で、
// 天気マークはここに挙げた項目にだけ付けられる（buildRowsのoverview参照）。
// 工程だけに絞ると、ワークグループ行と成果物一覧行で天気を選べなくなる。
// ワークグループ（SNAPSHOT_ROOT_UUID）は定義順の都合でbuildOverviewTargetCodes
// 内で先頭に足す。
const buildOverviewTargetCodes = (): string[] => [
  SNAPSHOT_ROOT_UUID,
  ...REQUIREMENTS_PHASE_ITEMS.filter(
    item => item.type === 'PROCESS' || item.type === 'DELIVERABLE_LIST'
  ).map(item => item.code),
]

type AggregatedValue = {
  count: { deliverable: number; task: number }
  workload: { deliverable: number; task: number }
}

const emptyAggregate = (): AggregatedValue => ({
  count: { deliverable: 0, task: 0 },
  workload: { deliverable: 0, task: 0 },
})

const addToAggregate = (
  target: AggregatedValue,
  isDeliverable: boolean,
  workload: number
): void => {
  const key = isDeliverable ? 'deliverable' : 'task'
  target.count[key] += 1
  target.workload[key] += workload
}

// 工数は時間で保持する。グリッド側で時間/日/月に換算される
// （aggregatedValueService.getFormattedWithUnitConversion）。
type DescendantLeaf = {
  uuid: string
  type: string
  displayName: string
  status: `${WbsItemStatus}`
  isDeliverable: boolean
  estimatedHour: number
  scheduledEndDate: string
  actualEndDate: string | null
  // コメントで遅延項目の担当者を名指しするために持つ。未設定の項目もある。
  assigneeName: string | undefined
  // 直近の親（成果物）の名前。タスク名は「レビュー」「初版作成」のように
  // それ単体では何の話か分からないものが多いため、コメントでは
  // 「データ一覧のレビュー」と親とセットで示す。
  parentName: string | undefined
}

// ある工程配下のDELIVERABLE/TASKを集める。進捗の分母になるのは
// 集計対象（成果物・タスク）だけで、工程・成果物一覧のような中間ノードは
// 自分自身の実績を持たないため除く。
const collectDescendantLeaves = (
  rootUuid: string,
  childrenByUuid: { [uuid: string]: string[] }
): DescendantLeaf[] => {
  const leaves: DescendantLeaf[] = []
  const visit = (uuid: string, parentName: string | undefined) => {
    const body = demoWbsItemBodies[uuid]
    if (!body) return
    const isDeliverable = body.type === 'DELIVERABLE'
    const isTask = body.type === 'TASK'
    if (isDeliverable || isTask) {
      leaves.push({
        parentName,
        uuid,
        type: body.type,
        displayName: body.displayName,
        status: body.status,
        isDeliverable,
        estimatedHour: body.estimatedHour ?? 0,
        scheduledEndDate: body.scheduledDate?.endDate ?? '',
        actualEndDate: body.actualDate?.endDate ?? null,
        assigneeName: body.assignee?.name,
      })
    }
    ;(childrenByUuid[uuid] ?? []).forEach(childUuid =>
      visit(childUuid, body.displayName)
    )
  }
  visit(rootUuid, undefined)
  return leaves
}

const buildChildrenByUuid = (): { [uuid: string]: string[] } => {
  const parentNodeByUuid = getDemoWbsItemParentNodeByUuid()
  const childrenByUuid: { [uuid: string]: string[] } = {}
  Object.keys(demoWbsItemBodies).forEach(uuid => {
    const parentUuid = parentNodeByUuid[uuid]?.wbsItemUuid
    if (!parentUuid) return
    ;(childrenByUuid[parentUuid] ??= []).push(uuid)
  })
  return childrenByUuid
}

const isClosed = (status: `${WbsItemStatus}`) =>
  status === WbsItemStatus.DONE || status === WbsItemStatus.DISCARD

// 集計カテゴリの定義。製品のグリッド列（総数/予定/完了/遅延/残/先行/未計画）に
// 対応する。基準日をbaseDateとして、レポート世代ごとに過去時点の進捗を表せる
// ようにしている（過去のレポートを開くと、その時点の数字が出る）。
const buildProgressCategories = (
  leaves: DescendantLeaf[],
  baseDate: string
) => {
  const total = emptyAggregate()
  const planned = emptyAggregate()
  const completed = emptyAggregate()
  const delay = emptyAggregate()
  const remaining = emptyAggregate()
  const preceding = emptyAggregate()
  const unplanned = emptyAggregate()

  leaves.forEach(leaf => {
    const { isDeliverable, estimatedHour, scheduledEndDate } = leaf
    const doneByBaseDate =
      isClosed(leaf.status) &&
      !!leaf.actualEndDate &&
      leaf.actualEndDate <= baseDate
    const shouldBeDone = !!scheduledEndDate && scheduledEndDate <= baseDate

    addToAggregate(total, isDeliverable, estimatedHour)
    if (shouldBeDone) addToAggregate(planned, isDeliverable, estimatedHour)
    if (doneByBaseDate) addToAggregate(completed, isDeliverable, estimatedHour)
    if (shouldBeDone && !doneByBaseDate) {
      addToAggregate(delay, isDeliverable, estimatedHour)
    }
    if (!doneByBaseDate) addToAggregate(remaining, isDeliverable, estimatedHour)
    if (doneByBaseDate && !shouldBeDone) {
      addToAggregate(preceding, isDeliverable, estimatedHour)
    }
    if (!scheduledEndDate) {
      addToAggregate(unplanned, isDeliverable, estimatedHour)
    }
  })

  return { total, planned, completed, delay, remaining, preceding, unplanned }
}

const buildPlannedCompletedDiff = (
  planned: AggregatedValue,
  completed: AggregatedValue
): AggregatedValue => ({
  count: {
    deliverable: completed.count.deliverable - planned.count.deliverable,
    task: completed.count.task - planned.count.task,
  },
  workload: {
    deliverable: completed.workload.deliverable - planned.workload.deliverable,
    task: completed.workload.task - planned.workload.task,
  },
})

// 天気ステータス（晴/曇/雨）は、予実の乖離から機械的に決める。
// レポート世代ごとに手で書いた固定値を持つと、日付マッピングで数値が
// 変わったときに「晴なのに大幅遅延」のような矛盾が出るため。
const resolveReportStatus = (
  planned: AggregatedValue,
  completed: AggregatedValue
): 'SUNNY' | 'CLOUDY' | 'RAINY' => {
  const plannedCount = planned.count.deliverable + planned.count.task
  const completedCount = completed.count.deliverable + completed.count.task
  if (plannedCount === 0) return 'SUNNY'
  const rate = completedCount / plannedCount
  if (rate >= 0.95) return 'SUNNY'
  if (rate >= 0.7) return 'CLOUDY'
  return 'RAINY'
}

// 定性コメントは、その行の実データ（遅延している項目・担当者・滞留日数）から
// 文章を組み立てる。ステータス別の固定文だと、同じ「曇り」でも状況の違いが
// 文面に出ず、グリッドの数字と読み比べたときに具体性が無く見えるため。
//
// 工数（時間）には触れない。グリッドの工数列は時間/日/月へ単位換算されて
// 表示される（aggregatedValueService）ため、時間を書くと表示単位を変えた
// ときに食い違う。
const countOf = (value: AggregatedValue): number =>
  value.count.deliverable + value.count.task

type ProgressCategories = ReturnType<typeof buildProgressCategories>

// 行の状況を「未着手 / 完了済み / 進行中」に振り分けるための判定。
// 率（%）は持たない。グリッドの率はヘッダーのトグルで分母が変わるため、
// 文章に埋め込むと表示とずれる（buildOverallSummarySentenceのコメント参照）。
// totalCountは合算件数で、これもトグル次第でグリッドの総数列とは一致しない。
// 「全体で何件ある工程か」という規模感を示す用途に限って使う。
type ProgressRates = {
  totalCount: number
  isCompleted: boolean
  isNotStarted: boolean
}

const toProgressRates = (categories: ProgressCategories): ProgressRates => {
  const totalCount = countOf(categories.total)
  const plannedCount = countOf(categories.planned)
  const completedCount = countOf(categories.completed)
  return {
    totalCount,
    isCompleted: totalCount > 0 && completedCount === totalCount,
    isNotStarted: completedCount === 0 && plannedCount === 0,
  }
}

// ある項目が属する工程（depth 0）の名前。遅延がどの工程に固まっているかを
// 示すために使う。祖先を辿らず、工程配下を集めた結果に含まれるかで判定する
// （親マップを逆向きに辿るより、既にある収集ロジックを再利用する方が
// 集計対象の定義とずれない）。
const resolveRootProcessName = (
  leafUuid: string,
  childrenByUuid: { [uuid: string]: string[] }
): string | undefined => {
  const code = DEMO_ROOT_PROCESS_CODES.find(processCode =>
    collectDescendantLeaves(processCode, childrenByUuid).some(
      leaf => leaf.uuid === leafUuid
    )
  )
  return code ? demoWbsItemBodies[code]?.displayName : undefined
}

// 遅延タスクを配下に抱えている成果物の数。成果物自身の予定終了日はまだ先でも、
// 配下の作業が遅れていれば期限内に着地しない可能性がある。DescendantLeafは
// 親の名前（parentName）を持つので、それを成果物の識別子として数える。
const countDeliverablesWithOverdueTasks = (
  overdueTasks: DescendantLeaf[],
  childrenByUuid: { [uuid: string]: string[] }
): number => {
  const deliverableUuids = new Set<string>()
  Object.keys(childrenByUuid).forEach(uuid => {
    if (demoWbsItemBodies[uuid]?.type !== 'DELIVERABLE') return
    const hasOverdueChild = collectDescendantLeaves(uuid, childrenByUuid).some(
      leaf => overdueTasks.some(task => task.uuid === leaf.uuid)
    )
    if (hasOverdueChild) deliverableUuids.add(uuid)
  })
  return deliverableUuids.size
}

// 期日を「過ぎて」未完了の項目。基準日当日が予定終了日のものは、まだ当日中に
// 終わりうるので超過に数えない。集計カテゴリのdelay（scheduledEndDate <= baseDate）
// とは意図的に条件を変えている——delayは当日ぶんも含むため、そのまま
// 「予定終了日を超過」と書くと、今日が期限で作業中の項目まで遅延として
// 名指ししてしまう（デモデータでは実際にこれが起きていた）。
const collectOverdueLeaves = (
  leaves: DescendantLeaf[],
  baseDate: string
): DescendantLeaf[] =>
  leaves
    .filter(
      leaf =>
        !isClosed(leaf.status) &&
        !!leaf.scheduledEndDate &&
        leaf.scheduledEndDate < baseDate
    )
    .sort((a, b) => a.scheduledEndDate.localeCompare(b.scheduledEndDate))

// グリッドの「遅延」列と同じ母集団。collectOverdueLeavesと違い、基準日当日が
// 予定終了日のものも含む（buildProgressCategoriesのdelayが
// scheduledEndDate <= baseDate で数えているため）。
//
// 件数をグリッドと並べて見せる箇所ではこちらを使う。「◯日経過」のように
// 遅れの長さを語る箇所は、当日ぶんを含めると「0日遅れ」になってしまうので
// collectOverdueLeavesの方を使う。
const collectDelayedLeaves = (
  leaves: DescendantLeaf[],
  baseDate: string
): DescendantLeaf[] =>
  leaves.filter(
    leaf =>
      !isClosed(leaf.status) &&
      !!leaf.scheduledEndDate &&
      leaf.scheduledEndDate <= baseDate
  )

// 基準日時点で「着手済みかつ未完了」の項目。予定終了日が未来という条件だけで
// 拾うと、まだ始まっていない先の項目まで作業中に数えてしまう（要件定義で
// 40件が同時進行という表示になっていた）。対応中・レビュー中に限定する。
const IN_FLIGHT_STATUSES: readonly `${WbsItemStatus}`[] = [
  WbsItemStatus.DOING,
  WbsItemStatus.REVIEW,
]

const collectInFlightLeaves = (leaves: DescendantLeaf[]): DescendantLeaf[] =>
  leaves.filter(leaf => IN_FLIGHT_STATUSES.includes(leaf.status))

// レビュー中の項目。作業自体は終わっていて承認だけが残っている状態は、
// 打ち手（レビュアーの確保）が対応中とは違うため分けて扱う。
const collectReviewLeaves = (leaves: DescendantLeaf[]): DescendantLeaf[] =>
  leaves.filter(leaf => leaf.status === WbsItemStatus.REVIEW)

// 項目名の表示。タスク名は「レビュー」「初版作成」のように単体では
// 意味を成さないものが多いため、親（成果物）とセットで示す。
// 多いと読みにくいので先頭2件までにし、残りは件数で示す。
const MAX_NAMED_ITEMS = 2

// 同じ成果物に複数の項目がぶら下がる場合、親名を繰り返すと
// 「「データ一覧」の項目整理、「データ一覧」のレビュー」と冗長になるため、
// 親でまとめて「「データ一覧」の項目整理・レビュー」の形にする。
const listItemNames = (leaves: DescendantLeaf[]): string => {
  const shown = leaves.slice(0, MAX_NAMED_ITEMS)
  const restCount = leaves.length - MAX_NAMED_ITEMS
  const groups: { parentName: string | undefined; names: string[] }[] = []
  shown.forEach(leaf => {
    const last = groups[groups.length - 1]
    if (last && last.parentName === leaf.parentName) {
      last.names.push(leaf.displayName)
      return
    }
    groups.push({ parentName: leaf.parentName, names: [leaf.displayName] })
  })
  const names = groups
    .map(group =>
      group.parentName
        ? `「${group.parentName}」の${group.names.join('・')}`
        : group.names.map(name => `「${name}」`).join('・')
    )
    .join('、')
  return restCount > 0 ? `${names}ほか${restCount}件` : names
}

// 同一担当に偏っている場合だけ名前を出す。複数人にまたがるなら
// 個人名を挙げても打ち手にならないため触れない。
const resolveSingleAssigneeName = (
  leaves: DescendantLeaf[]
): string | undefined => {
  const names = new Set(
    leaves
      .map(leaf => leaf.assigneeName)
      .filter((name): name is string => !!name)
  )
  return names.size === 1 ? [...names][0] : undefined
}

const MS_PER_DAY = 24 * 60 * 60 * 1000

// 最も長く滞留している項目の超過日数。進捗報告で最初に問われるのは
// 「どれだけ遅れているか」なので、率よりこちらを前に出す。
const maxOverdueDays = (
  overdue: DescendantLeaf[],
  baseDate: string
): number => {
  const base = new Date(baseDate).getTime()
  return overdue.reduce((longest, leaf) => {
    const days = Math.floor(
      (base - new Date(leaf.scheduledEndDate).getTime()) / MS_PER_DAY
    )
    return days > longest ? days : longest
  }, 0)
}

// 未着手工程の書き分け。デモの「今日」は要件定義の途中にあり、後続13工程は
// すべて未着手になる。同じ一文を13行並べると定型文にしか見えないため、
// 規模で状況の書き方を変える。ただし件数そのものは書かない——ここで数えて
// いるのは成果物とタスクの合算で、グリッドの「総数」列（集計対象トグルで
// どちらか一方のみ）とは一致しないため。
const LARGE_PROCESS_ITEM_COUNT = 20

const buildNotStartedComment = (totalCount: number): string => {
  if (totalCount === 0) {
    return `未着手です。実施範囲が未確定のため、確定次第あらためて計画へ反映します。`
  }
  if (totalCount >= LARGE_PROCESS_ITEM_COUNT) {
    return `未着手です。規模が大きいため、着手前に要員計画とスケジュールの妥当性をあらためて確認します。`
  }
  return `未着手です。先行工程の完了を待って着手します。`
}

// 行のコメント本文。
//
// 行名（工程名・チーム名）は含めない。すぐ隣の名称列に出ており、
// 「「要件定義」は〜」と書き出すと列の繰り返しになるため。
//
// 進捗率も原則書かない。左隣に計画率・実績率の列が並んでおり、同じ数字を
// 文章で繰り返しても情報が増えない。コメントでしか表せないこと——何が
// 遅れているか、何日遅れているか、誰が持っているか、次に何をするか——に絞る。
const buildRowComment = (
  rates: ProgressRates,
  leaves: DescendantLeaf[],
  baseDate: string
): string => {
  if (rates.isNotStarted) {
    return buildNotStartedComment(rates.totalCount)
  }

  const overdue = collectOverdueLeaves(leaves, baseDate)
  if (overdue.length > 0) {
    const assigneeName = resolveSingleAssigneeName(overdue)
    const delayDays = maxOverdueDays(overdue, baseDate)
    // 滞留が1件か複数かで、原因の見立ても打ち手も変わる。
    // 件数は書かず、項目名で示す。ここでの件数は成果物とタスクの合算だが、
    // グリッドの「遅延」列は集計対象トグルでどちらか一方しか数えないため、
    // 並べると数が合わない。名前なら数の比較が発生しない。
    const situation =
      overdue.length > 1
        ? `${listItemNames(overdue)}が未完了で、最長${delayDays}日の遅れとなっています。`
        : `${listItemNames(overdue)}が未完了のまま${delayDays}日が経過しています。`
    const action = assigneeName
      ? overdue.length > 1
        ? `いずれも${assigneeName}が担当しており、負荷の偏りが主因と見ています。次週は他メンバーへ再配分し、遅れの拡大を止めます。`
        : `${assigneeName}が対応中ですが単独では巻き返しが難しく、次週から支援要員を追加します。`
      : `担当者と残作業を洗い出したうえで、次週中に巻き返し計画を確定させます。`
    return `${situation}${action}`
  }

  if (rates.isCompleted) {
    return `予定していた項目をすべて完了し、成果物の顧客承認も取得済みです。次工程への引き継ぎ条件は整っています。`
  }

  // 期日超過はないが未完了が残る通常の進行中。何を作業しているかを示す。
  const inFlight = collectInFlightLeaves(leaves)
  if (inFlight.length > 0) {
    const reviewCount = collectReviewLeaves(inFlight).length
    const detail =
      reviewCount > 0
        ? `一部はレビュー段階に入っており、指摘反映を終えたものから順にクローズします。`
        : `いずれも予定終了日までに完了する見込みです。`
    return `期日を超過した項目はなく、計画どおりのペースです。現在は${listItemNames(inFlight)}などが進行中です。${detail}`
  }

  return `期日を超過した項目はなく、計画どおりに進んでいます。次の予定項目の着手準備を進めます。`
}

// 基準日（レポートが「いつ時点の集計か」）。世代0が最新。
const baseDateOfGeneration = (generation: number): string =>
  dayOffsetToDate(TODAY_DAY_OFFSET - generation * DAYS_PER_WEEK)

const takenAtOfGeneration = (generation: number): number =>
  dayOffsetToEpochMs(TODAY_DAY_OFFSET - generation * DAYS_PER_WEEK, 18)

const toIsoString = (epochMs: number): string => new Date(epochMs).toISOString()

export const DEMO_VERSION_REPORT_TEMPLATE = {
  uuid: TEMPLATE_UUID,
  name: TEMPLATE_NAME,
  get updatedAt() {
    return takenAtOfGeneration(0)
  },
  updatedBy: demoUser.name,
  // 画面から作成した分も数に入れるため、定数ではなく現在の件数を返す
  // （レポート選択メニューがテンプレート行に件数を出す）。
  get versionCount() {
    return DEMO_VERSION_SEEDS.length
  },
}

// バージョン（レポート世代）。世代0が最新で、ヘッダのレポート選択メニューに
// 新しい順で並ぶ。
type DemoVersionSeed = {
  generation: number
  uuid: string
  name: string
  snapshotUuid: string
}

const DEMO_VERSION_SEEDS: DemoVersionSeed[] = Array.from(
  { length: REPORT_GENERATION_COUNT },
  (_, generation) => ({
    generation,
    uuid: `demo-version-report-version-${generation}`,
    // 第N回は古い方から数えた方が自然なので、世代を反転させる。
    name: `第${REPORT_GENERATION_COUNT - generation}回 週次進捗報告`,
    snapshotUuid: `demo-version-report-snapshot-${generation}`,
  })
)

// 画面から作成したレポートもここに積む。lookupは実行時に引き直せるよう
// 定数マップではなく関数にする（モジュール評価時に固定すると、作成直後の
// レポートを開こうとしても「削除済み」と判定されて弾かれる）。
const findVersionSeed = (uuid: string): DemoVersionSeed | undefined =>
  DEMO_VERSION_SEEDS.find(seed => seed.uuid === uuid)

const findVersionSeedBySnapshot = (
  snapshotUuid: string
): DemoVersionSeed | undefined =>
  DEMO_VERSION_SEEDS.find(seed => seed.snapshotUuid === snapshotUuid)

// 画面の「報告書の新規作成」（NewVersionDialog）から呼ばれる。
// デモはインメモリのみで、リロードするとこのモジュールが再評価されて
// 初期の3世代に戻る（他のデモデータと同じ扱い）。
//
// 新規作成は「今の時点でスナップショットを取る」操作なので、基準日は最新世代と
// 同じ generation 0 にする。集計値・コメントはgeneration 0のものが再利用され、
// 画面上は最新回と同じ数字を持つ新しい報告書として振る舞う。
export const createDemoVersionReportVersion = (
  name: string
): { uuid: string; isInitial: boolean } => {
  const sequence = DEMO_VERSION_SEEDS.length
  const seed: DemoVersionSeed = {
    generation: 0,
    uuid: `demo-version-report-version-created-${sequence}`,
    name,
    snapshotUuid: `demo-version-report-snapshot-created-${sequence}`,
  }
  // 一覧は新しい順に並べるため先頭へ入れる。
  DEMO_VERSION_SEEDS.unshift(seed)
  return {
    uuid: seed.uuid,
    // テンプレートには既にページ構成があるため、ページ設定ダイアログを
    // 自動で開く必要はない（isInitial=trueだと開く）。
    isInitial: false,
  }
}

// 画面は削除後に一覧を取り直すため、配列から取り除くだけでよい。
export const deleteDemoVersionReportVersion = (uuid: string): void => {
  const index = DEMO_VERSION_SEEDS.findIndex(seed => seed.uuid === uuid)
  if (index < 0) return
  DEMO_VERSION_SEEDS.splice(index, 1)
}

// 一覧・ヘッダのどちらもseedのnameを見るため、ここを書き換えれば両方に効く。
export const renameDemoVersionReportVersion = (
  uuid: string,
  name: string
): void => {
  const seed = findVersionSeed(uuid)
  if (!seed) return
  seed.name = name
}

// デモ初期表示で開くレポート。実運用で最初に見るのは直近の回なので、
// 最新世代（generation 0 = 第3回 週次進捗報告）を選んでおく。
export const DEMO_INITIAL_VERSION_UUID = DEMO_VERSION_SEEDS[0].uuid

// レポート内のページの並び順。デモは工程別進捗状況を最初に見せたいので
// processを先頭にしている。本体はページ配列の先頭（currentPageIndexの初期値0）
// から開き、初期ページを指定する口がURLにもPAGE_STATEにも無いため、
// 見せたいページを先頭に置くことで初期表示を決めている。
// pageNumber（画面の「1/3」表示）もこの順で振られる。
const PAGE_KINDS = ['process', 'overview', 'team'] as const
type PageKind = (typeof PAGE_KINDS)[number]

// ページuuidは世代とページ種別から決める。templatePageUuidは世代をまたいで
// 同じにする（同じページの世代間比較・ディスカッションの引き継ぎが
// templatePageUuid単位のため）。並び順を変えてもキーは種別名で決まるので
// 影響しない。
const templatePageUuidOf = (kind: PageKind) =>
  `demo-version-report-template-page-${kind}`

// ページ・行のuuidはレポート（バージョン）ごとに一意にする。
// 世代だけで組むと、同じ世代から作られた別のレポート（画面から新規作成した
// もの）と同じuuidになり、編集内容が互いに混ざる。
const pageUuidOf = (versionUuid: string, kind: PageKind) =>
  `demo-version-report-page-${versionUuid}-${kind}`

const rowUuidOf = (versionUuid: string, kind: PageKind, targetUuid: string) =>
  `demo-version-report-row-${versionUuid}-${kind}-${targetUuid}`

// 構成編集で行が追加されたときに、その1行だけを組み立てる。
// 追加された行にも既定ページと同じ集計値・コメントが載るようにするため、
// 既定の行生成と同じ導出（WBSから集計 → 天気 → コメント）を通す。
// targetUuidはページ種別によってWBS項目のcodeだったりチームのuuidだったりする。
// ページのconfigJsonStringから集計範囲（rootUuid）を読む。未設定・壊れた
// JSONのときはワークグループ全体にフォールバックする（構成編集で範囲を
// 選ぶ前のページや、チーム別以外のページはrootUuidを持たない）。
const rootUuidOfConfig = (configJsonString: string): string => {
  try {
    return JSON.parse(configJsonString || '{}').rootUuid || SNAPSHOT_ROOT_UUID
  } catch {
    return SNAPSHOT_ROOT_UUID
  }
}

const buildRowForTarget = (
  generation: number,
  pageUuid: string,
  pageType: string,
  targetUuid: string,
  displayOrder: number,
  configJsonString: string
) => {
  const baseDate = baseDateOfGeneration(generation)
  const childrenByUuid = buildChildrenByUuid()
  const isTeamPage = pageType === PAGE_TYPES.team
  // チーム別は集計の起点をページのrootUuid（集計範囲）に揃える。
  // buildDemoTeamProgressValues（数値）と同じ母集団にしないと、天気・
  // 定性コメントの根拠が数値と食い違う。
  const leaves = isTeamPage
    ? collectDescendantLeaves(
        rootUuidOfConfig(configJsonString),
        childrenByUuid
      ).filter(leaf => demoWbsItemBodies[leaf.uuid]?.team?.uuid === targetUuid)
    : collectDescendantLeaves(targetUuid, childrenByUuid)
  const categories = buildProgressCategories(leaves, baseDate)
  return {
    // 行uuidはページ内で一意にする。pageTypeだけで組むと、同じ種別の
    // ページを追加したときに既存ページの行と同じuuidになる。
    uuid: `demo-version-report-row-${pageUuid}-${targetUuid}`,
    lockVersion: 1,
    targetUuid,
    displayOrder,
    status: resolveReportStatus(categories.planned, categories.completed),
    comment: buildRowComment(toProgressRates(categories), leaves, baseDate),
  }
}

// 進捗概況ページの行。天気マークは行（targetUuid）に紐づくpageRowがある
// 項目にしか付けられない（master-scheduleのhandleStatusChangeが
// pageRows.findで引けた行だけを更新する）ため、ツリーに出る項目すべて
// （ワークグループ・工程・成果物一覧）に行を作る。工程だけにすると、
// ワークグループ行と成果物一覧行で天気を選べない。
//
// 天気は工程別・チーム別ページと同じくその行配下の進捗から導出する。
// 全行SUNNY固定だと、遅延している工程にも晴が出てツリーの数値と矛盾する。
const buildOverviewRows = (versionUuid: string, generation: number) => {
  const baseDate = baseDateOfGeneration(generation)
  const childrenByUuid = buildChildrenByUuid()
  return buildOverviewTargetCodes().map((code, index) => {
    const leaves = collectDescendantLeaves(code, childrenByUuid)
    const categories = buildProgressCategories(leaves, baseDate)
    return {
      uuid: rowUuidOf(versionUuid, 'overview', code),
      lockVersion: 1,
      targetUuid: code,
      displayOrder: index + 1,
      status: resolveReportStatus(categories.planned, categories.completed),
      // 概況ページはツリーとガントで見せる画面で、行ごとの定性コメント欄を
      // 持たない（工程別・チーム別と違いコメント列が無い）。
      comment: '',
    }
  })
}

const buildProcessRows = (
  versionUuid: string,
  generation: number,
  kind: PageKind
) => {
  const childrenByUuid = buildChildrenByUuid()
  const baseDate = baseDateOfGeneration(generation)
  return DEMO_ROOT_PROCESS_CODES.map((code, index) => {
    const leaves = collectDescendantLeaves(code, childrenByUuid)
    const categories = buildProgressCategories(leaves, baseDate)
    const status = resolveReportStatus(categories.planned, categories.completed)
    return {
      uuid: rowUuidOf(versionUuid, kind, code),
      lockVersion: 1,
      targetUuid: code,
      displayOrder: index + 1,
      status,
      comment: buildRowComment(toProgressRates(categories), leaves, baseDate),
    }
  })
}

const TEAM_PAGE_ROOT_PROCESS = REQUIREMENTS_PHASE_ITEMS.find(
  item => item.depth === 0
)

const TEAM_PAGE_ROOT_UUID = TEAM_PAGE_ROOT_PROCESS?.code ?? SNAPSHOT_ROOT_UUID

const buildTeamRows = (versionUuid: string, generation: number) => {
  const baseDate = baseDateOfGeneration(generation)
  const childrenByUuid = buildChildrenByUuid()
  return demoTeams.map((team, index) => {
    // 起点はbuildDemoTeamProgressValues（数値）と揃える。
    const leaves = collectDescendantLeaves(
      TEAM_PAGE_ROOT_UUID,
      childrenByUuid
    ).filter(leaf => demoWbsItemBodies[leaf.uuid]?.team?.uuid === team.uuid)
    const categories = buildProgressCategories(leaves, baseDate)
    const status = resolveReportStatus(categories.planned, categories.completed)
    return {
      uuid: rowUuidOf(versionUuid, 'team', team.uuid),
      lockVersion: 1,
      targetUuid: team.uuid,
      displayOrder: index + 1,
      status,
      comment: buildRowComment(toProgressRates(categories), leaves, baseDate),
    }
  })
}

// チーム別だけ集計範囲の工程名を括弧で添える。数値がワークグループ全体ではなく
// 特定の工程に絞られていることを、グリッドを読む前にタイトルで分かるようにする。
const PAGE_TITLES: { [K in PageKind]: string } = {
  overview: '進捗概況',
  process: '工程別進捗状況',
  team: TEAM_PAGE_ROOT_PROCESS
    ? `チーム別進捗状況（${TEAM_PAGE_ROOT_PROCESS.name}工程）`
    : 'チーム別進捗状況',
}

// ページのリード文。全行を俯瞰した「総括」にあたる部分なので、固定文ではなく
// その世代の実数から組み立てる。画面側（PageHeader）は折りたたみ表示で、
// 展開すると全文が読めるため、数行の文章を入れてよい。
//
// ただし進捗率（%）は書かないこと。グリッドの率はヘッダーのトグル2つ
// （集計対象=成果物/タスク、集計項目=個数/工数）で分母が変わり、同じ基準日でも
// 4通りの値を取る（getFormattedRatePercentage → getTargetValueは成果物と
// タスクの片方しか見ず、合算しない）。一方リード文は保存済みの文字列で、
// トグルを切り替えても再生成されないため、どの値を書いてもいずれかの
// 表示状態と食い違う。
//
// そこでトグルに依存しない事実——期日を超過した項目の「件数」と「最長遅延
// 日数」——だけで組み立てる。超過判定は予定終了日と基準日の比較のみで、
// 集計対象・集計項目のどちらにも左右されない。
// 基準日時点で完了している項目の件数。世代間の増分（今週の消化数）を出すため、
// 完了判定だけを取り出して使い回す。
const countClosedByBaseDate = (
  leaves: DescendantLeaf[],
  baseDate: string
): number =>
  leaves.filter(
    leaf =>
      isClosed(leaf.status) &&
      !!leaf.actualEndDate &&
      leaf.actualEndDate <= baseDate
  ).length

const buildOverallSummarySentence = (generation: number): string => {
  const baseDate = baseDateOfGeneration(generation)
  const childrenByUuid = buildChildrenByUuid()
  const allLeaves = DEMO_ROOT_PROCESS_CODES.flatMap(code =>
    collectDescendantLeaves(code, childrenByUuid)
  )
  // 今週の消化数。グリッドは基準日時点の断面しか出せず、前回からどれだけ
  // 進んだかは画面のどこにも現れない。報告書で最初に共有したいのはこの
  // 「1週間で何件終わったか」なので、リード文の役割はここに置く。
  //
  // 累計の完了件数は出さない。成果物とタスクの合算になり、グリッドの「完了」
  // 列（トグルでどちらか一方のみ）と並べると数が合わないため。増分なら
  // 対応する列が画面に無く、読み比べて矛盾する相手がいない。
  const closedNow = countClosedByBaseDate(allLeaves, baseDate)
  const closedPrev = countClosedByBaseDate(
    allLeaves,
    baseDateOfGeneration(generation + 1)
  )
  const progress = `この1週間で${closedNow - closedPrev}件が新たに完了しました。`

  // ペースの判定は「期日を過ぎて未完了の項目があるか」で決める。
  // 計画件数と実績件数の差で見ると、基準日当日が期限で作業中の項目まで
  // 未達に数えてしまい、遅延が無いのに「計画を下回る」と書いてしまう。
  const overdue = collectOverdueLeaves(allLeaves, baseDate)
  if (collectDelayedLeaves(allLeaves, baseDate).length === 0) {
    return `${progress}遅延している項目はなく、計画どおりのペースで進行しています。`
  }
  // 遅れはタスクと成果物で意味が違うため、階層を分けて示す。
  // タスクは「作業が何日滞留しているか」、成果物は「納品物として期限内に
  // 着地しそうか」で、打ち手も報告相手も変わる。合算した1つの数字だと
  // どちらの話か分からず、グリッドの集計対象トグル（成果物/タスク）を
  // 切り替えて見ている人ともかみ合わない。
  //
  // 件数はグリッドの「遅延」列と並べて読まれるので、当日期限ぶんも含む
  // collectDelayedLeavesで数える。一方「最長◯日」はcollectOverdueLeaves
  // （当日を除く）側から取る——当日期限を混ぜると0日遅れが最長候補になり、
  // 「1件が期日を超過し、最長0日の遅れ」という無意味な文になるため。
  const delayed = collectDelayedLeaves(allLeaves, baseDate)
  const delayedTasks = delayed.filter(leaf => !leaf.isDeliverable)
  const delayedDeliverables = delayed.filter(leaf => leaf.isDeliverable)
  const overdueTasks = overdue.filter(leaf => !leaf.isDeliverable)
  const overdueDeliverables = overdue.filter(leaf => leaf.isDeliverable)

  const sentences: string[] = [progress]
  if (delayedTasks.length > 0) {
    const days = maxOverdueDays(overdueTasks, baseDate)
    sentences.push(
      `タスクレベルでは${delayedTasks.length}件が遅延しており、最長${days}日の遅れとなっています。`
    )
  }
  if (delayedDeliverables.length > 0) {
    const days = maxOverdueDays(overdueDeliverables, baseDate)
    sentences.push(
      days > 0
        ? `成果物レベルでも${delayedDeliverables.length}件が遅延しており（最長${days}日）、納期への影響が出ています。`
        : `成果物レベルでも${delayedDeliverables.length}件が遅延していますが、いずれも当日が期限のため、本日中の完了可否を確認します。`
    )
  } else {
    // 成果物自身の期限は未到来でも、配下に遅延タスクを抱えていれば着地は
    // 危うい。件数を出さずに「遅延なし」とだけ書くと安全側に誤読されるため、
    // 影響を受けている成果物の数を示す。
    const affected = countDeliverablesWithOverdueTasks(
      delayedTasks,
      childrenByUuid
    )
    sentences.push(
      affected > 0
        ? `成果物レベルでの遅延はありませんが、${affected}件の成果物が配下に遅延を抱えており、予定どおり着地できるか注視が必要です。`
        : '成果物レベルでの遅延はありません。'
    )
  }

  // 遅延が特定の工程に固まっているかどうかで打ち手が変わる。1工程に集中して
  // いるならそこを名指しする方が、件数だけを示すより次の行動に繋がる。
  const overdueProcessNames = [
    ...new Set(
      delayed
        .map(leaf => resolveRootProcessName(leaf.uuid, childrenByUuid))
        .filter((name): name is string => !!name)
    ),
  ]
  sentences.push(
    overdueProcessNames.length === 1
      ? `いずれも「${overdueProcessNames[0]}」に集中しており、当該工程の巻き返しが必要です。`
      : '該当工程の巻き返しが必要です。'
  )
  return sentences.join('')
}

// 工程別リード文の後半。全14工程を並べても大半が未着手で読みにくいだけなので、
// 「いま動いている工程」と「期日超過のある工程」に絞る。
const buildProcessHighlightSentence = (generation: number): string => {
  const baseDate = baseDateOfGeneration(generation)
  const childrenByUuid = buildChildrenByUuid()
  const completedNames: string[] = []
  const inFlightNames: string[] = []
  const overdueNames: string[] = []
  let notStartedCount = 0
  DEMO_ROOT_PROCESS_CODES.forEach(code => {
    const leaves = collectDescendantLeaves(code, childrenByUuid)
    const rates = toProgressRates(buildProgressCategories(leaves, baseDate))
    const name = demoWbsItemBodies[code]?.displayName ?? code
    if (rates.isNotStarted) {
      notStartedCount += 1
      return
    }
    if (collectOverdueLeaves(leaves, baseDate).length > 0) {
      overdueNames.push(name)
    } else if (rates.isCompleted) {
      completedNames.push(name)
    } else {
      inFlightNames.push(name)
    }
  })

  const quote = (names: string[]) => names.map(name => `「${name}」`).join('、')
  const sentences: string[] = []
  if (completedNames.length > 0) {
    sentences.push(`${quote(completedNames)}は予定分を完了しています。`)
  }
  if (inFlightNames.length > 0) {
    sentences.push(`現在は${quote(inFlightNames)}が進行中です。`)
  }
  if (overdueNames.length > 0) {
    sentences.push(
      `一方${quote(overdueNames)}には期日を超過した項目があり、重点管理項目として巻き返し状況を確認します。`
    )
  }
  if (notStartedCount > 0) {
    sentences.push(
      `残る${notStartedCount}工程は後続フェーズとして、先行工程の完了状況を見ながら着手予定です。`
    )
  }
  return sentences.join('')
}

// 進捗概況リード文の後半。このページはWBSツリーとガントで「計画全体のどこを
// 走っているか」を見せる画面なので、工程の消化状況（完了/進行中/未着手）を
// 示す。工程別ページのように工程名を列挙すると、すぐ下のツリーと同じ内容を
// 文章で繰り返すことになるため、件数の内訳にとどめる。
const buildOverviewHighlightSentence = (generation: number): string => {
  const baseDate = baseDateOfGeneration(generation)
  const childrenByUuid = buildChildrenByUuid()
  let completed = 0
  let inFlight = 0
  let notStarted = 0
  DEMO_ROOT_PROCESS_CODES.forEach(code => {
    const leaves = collectDescendantLeaves(code, childrenByUuid)
    const rates = toProgressRates(buildProgressCategories(leaves, baseDate))
    if (rates.isNotStarted) notStarted += 1
    else if (rates.isCompleted) completed += 1
    else inFlight += 1
  })
  const total = completed + inFlight + notStarted
  return `全${total}工程のうち完了${completed}・進行中${inFlight}・未着手${notStarted}という構成です。ガントチャート上の進捗線で、計画に対する現在の到達点を確認できます。`
}

// チーム別リード文の後半。このページでしか分からないのは「どのチームに
// 作業と遅れが寄っているか」なので、担当件数の偏りと遅延の所在を実数で示す。
// 固定の説明文（「要員の偏りを確認する目的で集計しています」）だけでは、
// 隣の工程別ページと同じ前置きが並ぶだけで、チーム別に切り替えた意味が出ない。
const buildTeamHighlightSentence = (generation: number): string => {
  const baseDate = baseDateOfGeneration(generation)
  const childrenByUuid = buildChildrenByUuid()
  const allLeaves = collectDescendantLeaves(TEAM_PAGE_ROOT_UUID, childrenByUuid)
  const teamOf = (leaf: DescendantLeaf) =>
    demoWbsItemBodies[leaf.uuid]?.team?.uuid

  // チーム×階層（成果物/タスク）で分けて持つ。このページのグリッドは
  // 「チームが行・集計対象トグルが列の内訳」という構造なので、文章も同じ
  // 切り口にすると読み手がセルと突き合わせられる。
  // 合算値を書かないのは、グリッドの「総数」列が集計対象トグルでどちらか
  // 一方しか出さず、合算だとどちらの表示とも一致しないため。
  const stats = demoTeams.map(team => {
    const leaves = allLeaves.filter(leaf => teamOf(leaf) === team.uuid)
    // 件数をグリッドの「遅延」列と並べて読ませるので、当日期限ぶんも含む
    // collectDelayedLeavesを使う（collectOverdueLeavesは当日を除くため
    // グリッドより少ない件数になる）。
    const delayedLeaves = collectDelayedLeaves(leaves, baseDate)
    const countBy = (items: DescendantLeaf[], isDeliverable: boolean) =>
      items.filter(leaf => leaf.isDeliverable === isDeliverable).length
    return {
      name: team.displayName,
      total: leaves.length,
      deliverable: countBy(leaves, true),
      task: countBy(leaves, false),
      delayedDeliverable: countBy(delayedLeaves, true),
      delayedTask: countBy(delayedLeaves, false),
    }
  })
  const assigned = stats.filter(stat => stat.total > 0)
  if (assigned.length === 0) return ''

  const sentences: string[] = [
    `担当量は${assigned
      .map(
        stat =>
          `${stat.name}が成果物${stat.deliverable}件・タスク${stat.task}件`
      )
      .join('、')}です。`,
  ]

  // 遅延も階層ごとに示す。成果物とタスクで打ち手が変わる（成果物の遅延は
  // 納期直撃、タスクの遅延はまだ内部で吸収できる余地がある）ため、
  // まとめて1つの数字にすると判断材料にならない。
  const delayed = assigned.filter(
    stat => stat.delayedDeliverable + stat.delayedTask > 0
  )
  if (delayed.length === 0) {
    sentences.push('遅延している項目はいずれのチームにもありません。')
  } else {
    const detail = delayed
      .map(stat => {
        const parts: string[] = []
        if (stat.delayedDeliverable > 0) {
          parts.push(`成果物${stat.delayedDeliverable}件`)
        }
        if (stat.delayedTask > 0) parts.push(`タスク${stat.delayedTask}件`)
        return `${stat.name}が${parts.join('・')}`
      })
      .join('、')
    const heaviest = [...delayed].sort(
      (a, b) =>
        b.delayedDeliverable +
        b.delayedTask -
        (a.delayedDeliverable + a.delayedTask)
    )[0]
    sentences.push(
      delayed.length === 1
        ? `遅延しているのは${detail}のみで、同チームへの支援を優先します。`
        : `遅延は${detail}で、件数の多い${heaviest.name}への支援を優先します。`
    )
  }
  return sentences.join('')
}

const buildLeadSentence = (kind: PageKind, generation: number): string => {
  switch (kind) {
    case 'overview':
      return `${buildOverallSummarySentence(generation)}${buildOverviewHighlightSentence(generation)}`
    case 'process':
      return `${buildOverallSummarySentence(generation)}${buildProcessHighlightSentence(generation)}`
    case 'team':
      return `${buildOverallSummarySentence(generation)}${buildTeamHighlightSentence(generation)}`
  }
}

const PAGE_TYPES: { [K in PageKind]: string } = {
  overview: 'PROGRESS_OVERVIEW',
  process: 'PROGRESS_REPORT_BY_PROCESS',
  team: 'PROGRESS_REPORT_BY_TEAM',
}

const buildConfigJsonString = (kind: PageKind): string => {
  if (kind === 'team') {
    return JSON.stringify({
      scopeType: 'PROCESS',
      rootUuid: TEAM_PAGE_ROOT_UUID,
    })
  }
  return JSON.stringify({})
}

const buildRows = (versionUuid: string, generation: number, kind: PageKind) => {
  switch (kind) {
    case 'overview':
      return buildOverviewRows(versionUuid, generation)
    case 'process':
      return buildProcessRows(versionUuid, generation, 'process')
    case 'team':
      return buildTeamRows(versionUuid, generation)
  }
}

type DemoVersionPage = {
  uuid: string
  lockVersion: number
  templatePageUuid: string
  pageTitle: string
  leadSentence: string
  pageNumber: number
  pageType: string
  configJsonString: string
  rows: ReturnType<typeof buildRows>
}

const buildDefaultPages = (
  versionUuid: string,
  generation: number
): DemoVersionPage[] =>
  PAGE_KINDS.map((kind, index) => ({
    uuid: pageUuidOf(versionUuid, kind),
    lockVersion: 1,
    templatePageUuid: templatePageUuidOf(kind),
    pageTitle: PAGE_TITLES[kind],
    leadSentence: buildLeadSentence(kind, generation),
    pageNumber: index + 1,
    pageType: PAGE_TYPES[kind],
    configJsonString: buildConfigJsonString(kind),
    rows: buildRows(versionUuid, generation, kind),
  }))

// 画面のページ構成編集（VersionConfigDialog → applyStructureChanges）の結果。
// ページはfetchのたびにWBSから組み直す方式なので、編集結果を保持する場所が
// ないと、ダイアログを閉じた直後の再取得で元に戻ってしまう。
// 編集があったバージョンだけ、組み立て済みのページ一覧をここへ退避する。
const editedPagesByVersionUuid: { [versionUuid: string]: DemoVersionPage[] } =
  {}

const buildPages = (uuid: string, generation: number): DemoVersionPage[] =>
  editedPagesByVersionUuid[uuid] ?? buildDefaultPages(uuid, generation)

export type DemoStructureChanges = {
  versionUuid: string
  added?: {
    pageType: string
    pageNumber: number
    pageTitle: string
    configJsonString: string
    initialRows?: { targetUuid: string; displayOrder: number }[]
  }[]
  updated?: {
    pageUuid: string
    pageNumber: number
    pageTitle: string
    configJsonString: string
    // 行の識別子はuuidではなくrowUuid（ports/versionReportVersionRepositoryの
    // DeletedRow / ReorderRow）。uuidで読むと削除・並べ替えが一切効かない。
    deletedRows?: { rowUuid: string }[]
    addedRows?: { targetUuid: string; displayOrder: number }[]
    reorderedRows?: { rowUuid: string; displayOrder: number }[]
  }[]
  deletedPages?: { uuid: string }[]
}

let addedPageSequence = 0

// 追加ページの行は、既定ページと同じくWBSから集計した値で作る
// （画面はtargetUuidしか送ってこないので、数値・コメントはこちらで導出する）。
export const applyDemoStructureChanges = (
  changes: DemoStructureChanges
): void => {
  const seed = findVersionSeed(changes.versionUuid)
  if (!seed) return
  const pages = [...buildPages(changes.versionUuid, seed.generation)]

  const deletedUuids = new Set((changes.deletedPages ?? []).map(p => p.uuid))
  let next = pages.filter(page => !deletedUuids.has(page.uuid))

  ;(changes.updated ?? []).forEach(update => {
    const index = next.findIndex(page => page.uuid === update.pageUuid)
    if (index < 0) return
    const page = next[index]
    const removed = new Set((update.deletedRows ?? []).map(r => r.rowUuid))
    const displayOrderByUuid = new Map(
      (update.reorderedRows ?? []).map(r => [r.rowUuid, r.displayOrder])
    )
    const keptRows = page.rows
      .filter(row => !removed.has(row.uuid))
      .map(row => ({
        ...row,
        displayOrder: displayOrderByUuid.get(row.uuid) ?? row.displayOrder,
      }))
    const addedRows = (update.addedRows ?? []).map(added =>
      buildRowForTarget(
        seed.generation,
        page.uuid,
        page.pageType,
        added.targetUuid,
        added.displayOrder,
        // 行の追加と同時に集計範囲が変わることがあるため、保存後の
        // configJsonStringを使う（page.configJsonStringは変更前の値）。
        update.configJsonString
      )
    )
    next[index] = {
      ...page,
      lockVersion: page.lockVersion + 1,
      pageTitle: update.pageTitle,
      pageNumber: update.pageNumber,
      configJsonString: update.configJsonString,
      rows: [...keptRows, ...addedRows].sort(
        (a, b) => a.displayOrder - b.displayOrder
      ),
    }
  })
  ;(changes.added ?? []).forEach(added => {
    addedPageSequence += 1
    const uuid = `demo-version-report-page-added-${addedPageSequence}`
    next.push({
      uuid,
      lockVersion: 1,
      // 世代間比較の単位。追加ページは既存ページと対応付かないので独自のidにする。
      templatePageUuid: `demo-version-report-template-page-added-${addedPageSequence}`,
      pageTitle: added.pageTitle,
      leadSentence: '',
      pageNumber: added.pageNumber,
      pageType: added.pageType,
      configJsonString: added.configJsonString,
      rows: (added.initialRows ?? []).map(row =>
        buildRowForTarget(
          seed.generation,
          uuid,
          added.pageType,
          row.targetUuid,
          row.displayOrder,
          added.configJsonString
        )
      ),
    })
  })

  // pageNumber・displayOrderは1始まりで詰め直す。画面から来る追加行の
  // displayOrderは0始まり（buildInitialRows）で、既定ページの行（1始まり）と
  // 混ざると並び順が崩れるため、保存前にこちらで正規化する。
  next = next
    .sort((a, b) => a.pageNumber - b.pageNumber)
    .map((page, index) => ({
      ...page,
      pageNumber: index + 1,
      rows: [...page.rows]
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((row, rowIndex) => ({ ...row, displayOrder: rowIndex + 1 })),
    }))
  editedPagesByVersionUuid[changes.versionUuid] = next
}

type DemoReportStatus = 'SUNNY' | 'CLOUDY' | 'RAINY'

const REPORT_STATUSES: readonly DemoReportStatus[] = [
  'SUNNY',
  'CLOUDY',
  'RAINY',
]

// 保存で届く天気は任意の文字列として来るため、既知の3値だけ受け取る。
// 未知の値をそのまま保持すると、天気アイコンの解決に失敗する。
const toReportStatus = (
  value: string | undefined,
  fallback: DemoReportStatus
): DemoReportStatus =>
  REPORT_STATUSES.find(status => status === value) ?? fallback

export type DemoSaveBatch = {
  versionUuid: string
  pages?: {
    versionPageUuid: string
    pageTitle?: { newValue?: string }
    leadSentence?: { newValue?: string }
  }[]
  rows?: {
    rowUuid: string
    status?: { newValue?: string }
    comment?: { newValue?: string }
    displayOrder?: { newValue?: number }
  }[]
}

// 定性コメント・天気・ページタイトル・リード文の保存（PUT .../version/batch）。
//
// 画面は保存レスポンスの lockVersion を自分が持っている値へ被せるだけで、
// 本文は手元の編集内容をそのまま確定させる。そのため保存直後の表示は
// モックが何もしなくても正しく見える。ただしページを切り替えたり
// レポートを開き直すと version を取り直すため、ここで保持しておかないと
// 編集内容が消える（ページはfetchのたびにWBSから組み直す方式のため）。
//
// 変更は「編集後のページ一覧」として構成編集と同じ置き場に退避する。
export const applyDemoSaveBatch = (
  batch: DemoSaveBatch
): {
  pages: { uuid: string; lockVersion: number }[]
  rows: { uuid: string; lockVersion: number }[]
} => {
  const seed = findVersionSeed(batch.versionUuid)
  if (!seed) return { pages: [], rows: [] }

  const pageEdits = new Map(
    (batch.pages ?? []).map(page => [page.versionPageUuid, page])
  )
  const rowEdits = new Map((batch.rows ?? []).map(row => [row.rowUuid, row]))

  const savedPages: { uuid: string; lockVersion: number }[] = []
  const savedRows: { uuid: string; lockVersion: number }[] = []

  const next = buildPages(batch.versionUuid, seed.generation).map(page => {
    const pageEdit = pageEdits.get(page.uuid)
    const rows = page.rows.map(row => {
      const rowEdit = rowEdits.get(row.uuid)
      if (!rowEdit) return row
      const lockVersion = row.lockVersion + 1
      savedRows.push({ uuid: row.uuid, lockVersion })
      return {
        ...row,
        lockVersion,
        // deltaはoldValue/newValueの組で届く。newValueが未指定なら未変更。
        status: toReportStatus(rowEdit.status?.newValue, row.status),
        comment: rowEdit.comment?.newValue ?? row.comment,
        displayOrder: rowEdit.displayOrder?.newValue ?? row.displayOrder,
      }
    })
    if (!pageEdit) return { ...page, rows }
    const lockVersion = page.lockVersion + 1
    savedPages.push({ uuid: page.uuid, lockVersion })
    return {
      ...page,
      lockVersion,
      pageTitle: pageEdit.pageTitle?.newValue ?? page.pageTitle,
      leadSentence: pageEdit.leadSentence?.newValue ?? page.leadSentence,
      rows,
    }
  })

  editedPagesByVersionUuid[batch.versionUuid] = next
  // 画面はこのlockVersionを手元の行へ被せる。返さないと次回保存で
  // 楽観ロックの不整合（CONSISTENCY_VIOLATION）扱いになりうる。
  return { pages: savedPages, rows: savedRows }
}

// versionPageUuid から、そのページが属するレポートとテンプレートページを引く。
// ディスカッションAPIはversionPageUuidしか受け取らないが、モック側は
// 「どのレポートの、どのページか」を知らないとスレッドを正しく振り分けられない。
export const findDemoVersionPageContext = (
  versionPageUuid: string
): { versionUuid: string; templatePageUuid: string } | undefined => {
  for (const seed of DEMO_VERSION_SEEDS) {
    const page = buildPages(seed.uuid, seed.generation).find(
      p => p.uuid === versionPageUuid
    )
    if (page) {
      return {
        versionUuid: seed.uuid,
        templatePageUuid: page.templatePageUuid,
      }
    }
  }
  return undefined
}

// レポート名の引き当て。対象別スレッド一覧が「どの回の議論か」を出すため。
export const demoVersionNameByUuid = (): { [uuid: string]: string } =>
  Object.fromEntries(DEMO_VERSION_SEEDS.map(seed => [seed.uuid, seed.name]))

// 対象（工程・チーム）の表示名と種別。スレッドに紐づけた対象のチップに使う。
export const demoDiscussionTargetByUuid = (): {
  [uuid: string]: { name: string; type: 'PROCESS' | 'TEAM' }
} => {
  const targets: {
    [uuid: string]: { name: string; type: 'PROCESS' | 'TEAM' }
  } = {}
  DEMO_ROOT_PROCESS_CODES.forEach(code => {
    const body = demoWbsItemBodies[code]
    if (body) targets[code] = { name: body.displayName, type: 'PROCESS' }
  })
  demoTeams.forEach(team => {
    targets[team.uuid] = { name: team.displayName, type: 'TEAM' }
  })
  return targets
}

const demoUserRef = { uuid: demoUser.uuid, name: demoUser.name }

// バージョン一覧（GET .../version/list）用。pagesは一覧では読まれないため空。
//
// ページ別スレッド件数はこちらに載せる。詳細（GET .../version）に載せても
// アダプタ（versionReportVersionRepositoryAdaptorのfetch）がホワイトリストで
// 組み直す際に捨てられ、読み手（VersionTimelineViewの
// getPastVersionThreadCount）は一覧由来のpastVersionsを見るため。
export const buildDemoVersionReportVersionList = () =>
  DEMO_VERSION_SEEDS.map(seed => ({
    uuid: seed.uuid,
    lockVersion: 1,
    snapshotUuid: seed.snapshotUuid,
    templateUuid: TEMPLATE_UUID,
    templateName: TEMPLATE_NAME,
    name: seed.name,
    pages: [],
    createdAt: toIsoString(takenAtOfGeneration(seed.generation)),
    createdBy: demoUserRef,
    updatedAt: toIsoString(takenAtOfGeneration(seed.generation)),
    updatedBy: demoUserRef,
    takenAt: toIsoString(takenAtOfGeneration(seed.generation)),
    discussionThreadCountByTemplatePage: countDemoThreadsByTemplatePage(
      seed.uuid,
      PAGE_KINDS.map(kind => templatePageUuidOf(kind))
    ),
  }))

// バージョン詳細（GET .../version）用。こちらはpagesまで含める。
export const buildDemoVersionReportVersion = (uuid: string) => {
  const seed = findVersionSeed(uuid)
  if (!seed) return undefined
  const takenAt = toIsoString(takenAtOfGeneration(seed.generation))
  return {
    uuid: seed.uuid,
    lockVersion: 1,
    snapshotUuid: seed.snapshotUuid,
    templateUuid: TEMPLATE_UUID,
    templateName: TEMPLATE_NAME,
    name: seed.name,
    pages: buildPages(seed.uuid, seed.generation),
    createdAt: takenAt,
    createdBy: demoUserRef,
    updatedAt: takenAt,
    updatedBy: demoUserRef,
    takenAt,
  }
}

const generationOfSnapshot = (snapshotUuid: string): number =>
  findVersionSeedBySnapshot(snapshotUuid)?.generation ?? 0

// 工程別ページの数値（GET .../snapshot/pivot/project-plans）。
export const buildDemoProcessProgressValues = (
  snapshotUuid: string,
  projectPlanUuids: string[]
) => {
  const baseDate = baseDateOfGeneration(generationOfSnapshot(snapshotUuid))
  const childrenByUuid = buildChildrenByUuid()
  return projectPlanUuids
    .filter(uuid => !!demoWbsItemBodies[uuid])
    .map(uuid => {
      const body = demoWbsItemBodies[uuid]
      const leaves = collectDescendantLeaves(uuid, childrenByUuid)
      const categories = buildProgressCategories(leaves, baseDate)
      return {
        uuid,
        name: body.displayName,
        ...categories,
        wbsItemStatus: body.status,
        team: body.team,
        plannedCompletedDiff: buildPlannedCompletedDiff(
          categories.planned,
          categories.completed
        ),
        scheduledDate: body.scheduledDate,
        maxChildScheduledEndDate: leaves.reduce(
          (latest, leaf) =>
            leaf.scheduledEndDate > latest ? leaf.scheduledEndDate : latest,
          ''
        ),
      }
    })
}

// チーム別ページの数値（GET .../snapshot/pivot/teams）。
export const buildDemoTeamProgressValues = (
  snapshotUuid: string,
  rootUuid: string,
  teamUuids: string[]
) => {
  const baseDate = baseDateOfGeneration(generationOfSnapshot(snapshotUuid))
  const childrenByUuid = buildChildrenByUuid()
  // 集計の起点は画面が送ってくるrootUuid（ページ構成編集で選んだ集計範囲）に
  // 合わせる。数値セルのドリルダウン（DeliverableTaskPopper）は同じrootUuidから
  // buildDemoSnapshotDescendantsで明細を引くため、ここを固定値にすると
  // 範囲を絞ったときにグリッドの数値だけ全体のまま残り、セルの数と明細の
  // 件数が食い違う。
  const allLeaves = collectDescendantLeaves(
    rootUuid || SNAPSHOT_ROOT_UUID,
    childrenByUuid
  )
  return teamUuids
    .map(teamUuid => demoTeams.find(team => team.uuid === teamUuid))
    .filter((team): team is (typeof demoTeams)[number] => !!team)
    .map(team => {
      const leaves = allLeaves.filter(
        leaf => demoWbsItemBodies[leaf.uuid]?.team?.uuid === team.uuid
      )
      const categories = buildProgressCategories(leaves, baseDate)
      return {
        uuid: team.uuid,
        name: team.displayName,
        iconUrl: team.iconUrl,
        ...categories,
        wbsItemStatus: '',
        team,
        plannedCompletedDiff: buildPlannedCompletedDiff(
          categories.planned,
          categories.completed
        ),
      }
    })
}

// 進捗概況ページのWBS（GET .../snapshot/leaf）。
//
// 返すのは「ツリー」ではなくフラットな配列。画面はAgGridのtreeDataで描画し、
// 行の階層は getDataPath（BulkSheetView）が各行の path を読んで決める
// （useMasterScheduleDataはレスポンスをmapするだけでchildrenを辿らない）。
// childrenに子をぶら下げただけの入れ子を返すと、ルートの14工程しか行にならず
// 配下のWBSがまったく表示されない。
//
// path は祖先の並び（自分自身を末尾に含む）。先頭は必ずワークグループ行
// （SNAPSHOT_ROOT_UUID）にする。そうしないと各工程がワークグループの
// 見た目になる。
export const buildDemoSnapshotTree = (
  snapshotUuid: string,
  leafUuids: string[]
) => {
  const baseDate = baseDateOfGeneration(generationOfSnapshot(snapshotUuid))
  const childrenByUuid = buildChildrenByUuid()

  const toSnapshotRow = (uuid: string, path: string[]) => {
    const body = demoWbsItemBodies[uuid]
    const leaves = collectDescendantLeaves(uuid, childrenByUuid)
    const categories = buildProgressCategories(leaves, baseDate)
    return {
      uuid,
      parentUuid: path[path.length - 2],
      path,
      type: body.type,
      wbsItem: {
        uuid,
        displayName: body.displayName,
        status: body.status,
        teamUuid: body.team?.uuid,
        estimatedHour: body.estimatedHour ?? 0,
        scheduledDate: body.scheduledDate ?? {},
        actualDate: body.actualDate ?? {},
        ticketType: body.ticketType,
        ticketListUuid: body.ticketListUuid,
      },
      progressValue: {
        uuid,
        name: body.displayName,
        ...categories,
        wbsItemStatus: body.status,
        team: body.team,
        plannedCompletedDiff: buildPlannedCompletedDiff(
          categories.planned,
          categories.completed
        ),
        scheduledDate: body.scheduledDate,
        maxChildScheduledEndDate: leaves.reduce(
          (latest, leaf) =>
            leaf.scheduledEndDate > latest ? leaf.scheduledEndDate : latest,
          ''
        ),
      },
      // 階層はpathで表現するため常に空。画面もchildrenは読まない。
      children: [],
    }
  }

  const rows: ReturnType<typeof toSnapshotRow>[] = []
  // 同じ項目を2行返さない。AgGridのtreeDataは行のpathで階層を組むため、
  // uuidが重複すると同じノードに複数の行が対応し、フィラー行
  // （data: undefined のグループ行）が生まれる。セルの描画は
  // params.data! を前提にしているので、そこで undefined を踏んで画面が落ちる。
  const visited = new Set<string>()
  const visit = (uuid: string, ancestors: string[]) => {
    const body = demoWbsItemBodies[uuid]
    if (!body || visited.has(uuid)) return
    // 表示するのは成果物一覧より上の階層（ワークグループ・工程・成果物一覧）
    // まで。成果物・タスクは集計されて数値列に現れる側で、行としては出さない。
    // ここで止めないとツリーがタスクまで展開され、進捗概況が計画画面と同じ
    // 粒度になってしまう。
    if (!SELECTABLE_SCOPE_TYPES.includes(body.type)) return
    visited.add(uuid)
    const path = [...ancestors, uuid]
    rows.push(toSnapshotRow(uuid, path))
    ;(childrenByUuid[uuid] ?? []).forEach(childUuid => visit(childUuid, path))
  }

  // ワークグループ行自体も1行として必要（path.length === 1 の行が無いと、
  // 配下の行のpathが指す親が存在せずAgGridがツリーを組み立てられない）。
  // ここでvisitedに入れておくことで、leafUuidsにワークグループ自身が
  // 含まれていても「自分の下に自分がぶら下がる」pathを作らない
  // （ページ構成編集でワークグループを行に選ぶとこれが起きる）。
  if (demoWbsItemBodies[SNAPSHOT_ROOT_UUID]) {
    visited.add(SNAPSHOT_ROOT_UUID)
    rows.push(toSnapshotRow(SNAPSHOT_ROOT_UUID, [SNAPSHOT_ROOT_UUID]))
  }
  leafUuids.forEach(uuid => visit(uuid, [SNAPSHOT_ROOT_UUID]))
  return rows
}

// チーム選択・ドリルダウンポッパーが参照するチーム一覧
// （GET .../snapshot/referenced-values?propertyName=TEAM_ENTITY）。
export const buildDemoSnapshotTeamReferencedValues = () =>
  demoTeams.map(team => ({
    value: team.uuid,
    name: team.displayName,
    iconUrl: team.iconUrl,
  }))

// 工程・チケット管理表の選択リスト（GET .../snapshot/master-schedule）。
export const buildDemoSnapshotMasterSchedule = () => {
  const childrenByUuid = buildChildrenByUuid()

  const toMasterScheduleRow = (
    uuid: string,
    body: (typeof demoWbsItemBodies)[string],
    path: string[]
  ) => ({
    uuid,
    parentUuid: path[path.length - 2],
    path,
    type: body.type,
    wbsItem: {
      uuid,
      displayName: body.displayName,
      status: body.status,
      estimatedHour: body.estimatedHour ?? 0,
      scheduledDate: body.scheduledDate ?? {},
      actualDate: body.actualDate ?? {},
      ticketType: body.ticketType,
    },
    children: [],
  })

  const rows: ReturnType<typeof toMasterScheduleRow>[] = []
  const visit = (uuid: string, path: string[]) => {
    const body = demoWbsItemBodies[uuid]
    if (!body) return
    // 集計範囲に選べるのは成果物一覧より上の階層（ワークグループ・工程・
    // 成果物一覧）だけ。成果物・タスクは集計の対象であって範囲にはならない
    // ため、選択リストに出さない。配下も同じく対象外なので辿らない。
    if (!SELECTABLE_SCOPE_TYPES.includes(body.type)) return
    const currentPath = [...path, uuid]
    rows.push(toMasterScheduleRow(uuid, body, currentPath))
    ;(childrenByUuid[uuid] ?? []).forEach(childUuid =>
      visit(childUuid, currentPath)
    )
  }
  visit(SNAPSHOT_ROOT_UUID, [])
  return rows
}

// ドリルダウンポッパー（数値セルクリック）の明細（GET .../snapshot/descendants）。
export const buildDemoSnapshotDescendants = (
  snapshotUuid: string,
  rootUuid: string
) => {
  const baseDate = baseDateOfGeneration(generationOfSnapshot(snapshotUuid))
  const childrenByUuid = buildChildrenByUuid()
  const leaves = collectDescendantLeaves(rootUuid, childrenByUuid)
  return leaves.map(leaf => {
    const doneByBaseDate =
      isClosed(leaf.status) &&
      !!leaf.actualEndDate &&
      leaf.actualEndDate <= baseDate
    const shouldBeDone =
      !!leaf.scheduledEndDate && leaf.scheduledEndDate <= baseDate
    const categories: string[] = ['TOTAL']
    if (shouldBeDone) categories.push('PLANNED')
    if (doneByBaseDate) categories.push('COMPLETED')
    if (shouldBeDone && !doneByBaseDate) categories.push('DELAY')
    if (!doneByBaseDate) categories.push('REMAINING')
    if (doneByBaseDate && !shouldBeDone) categories.push('PRECEDING')
    if (!leaf.scheduledEndDate) categories.push('UNPLANNED')
    const body = demoWbsItemBodies[leaf.uuid]
    return {
      uuid: leaf.uuid,
      type: leaf.type,
      wbsItem: {
        uuid: leaf.uuid,
        displayName: leaf.displayName,
        status: leaf.status,
        teamUuid: body?.team?.uuid,
        estimatedHour: leaf.estimatedHour,
        scheduledDate: body?.scheduledDate ?? {},
        actualDate: body?.actualDate ?? {},
      },
      categories,
      path: [rootUuid, leaf.uuid],
    }
  })
}

// 進捗概況ページのガントチャート初期表示（GET /api/v1/ui_states の
// VERSION_REPORT_DISPLAY_STATE-<templatePageUuid>）。
//
// この画面のガント設定は他画面と経路が違う。プロジェクト計画・ガント画面は
// Reduxのproject.ganttParameter（useOverrideDemoGanttParameter）を見るが、
// 進捗レポートはページ単位の表示状態として自前のUIStateキーから読む
// （hooks/pageDisplayState.ts）。そのためデモの初期値もこちらで与える。
//
// 保存値が無い場合、製品側はプロジェクトの予定期間（約17ヶ月）から表示単位を
// 自動決定するため四半期単位になる。週単位で見せたいので保存値として渡す。
//
// 4ヶ月（約17週）分を表示する。週次の進捗報告書なので、目盛りも週単位に
// 合わせる。期間は「今日」と進行中の要件定義が収まる長さにした。
// ワークグループ開始日から1〜2ヶ月では今日の手前で切れてしまい、進捗線
// （計画に対する現在の到達点）を見せられない。
const GANTT_DISPLAY_MONTHS = 4

// ワークグループ行（WBSツリーの最上位）の開始日。ガントの左端をここに合わせる
// ことで、WBSの先頭とガントの起点が一致する。
// 週単位表示では製品側（GanttParameterVO.roundStartDate）がこの日を含む週の
// 月曜へ丸めるため、実際の左端は数日手前になる。開始日が最初の週の目盛りに
// 入るという意図は変わらない。
const ganttRangeStart = (): number => dayOffsetToEpochMs(DEMO_PLAN_START_DAY, 0)

const ganttRangeEnd = (): number => {
  const start = new Date(ganttRangeStart())
  const end = new Date(start)
  end.setMonth(end.getMonth() + GANTT_DISPLAY_MONTHS)
  return end.getTime()
}

export const buildDemoVersionReportDisplayState = () => ({
  // GanttParameterVO.serialize と同じ形。range は epoch ミリ秒
  // （DateVO.toNumberValue）で持つ。
  ganttParameter: {
    unit: 'WEEK',
    // 週単位の目盛りを月曜始まりにする。DayOfWeekは文字列enumなので数値を
    // 入れないこと（mapDayOfWeekToNumberがswitchを素通りしてundefinedを返し、
    // roundStartDateの減算がNaNになって左端が壊れる）。
    startDay: 'MONDAY',
    range: {
      start: ganttRangeStart(),
      end: ganttRangeEnd(),
    },
    // GanttParameterVO.defaultDisplayOptions() と同じ項目を明示的に持つ。
    // 一部だけ指定してもコンストラクタが既定値で補完するが、
    // 既定値が変わったときにデモの見え方が黙って変わるのを避ける。
    displayOptions: {
      showTypeIcon: true,
      showProgressRate: true,
      showName: true,
      showAssignee: true,
      showStatus: true,
      // レポートは「計画に対する現在の到達点」を見せる画面なので進捗線を出す。
      showProgressLine: true,
      showMilestoneName: true,
      milestoneNameResetSeq: 0,
      lockRange: false,
      // 有効だとWBSルートの予定期間（約17ヶ月）から単位が再計算され、
      // ここで指定した週単位が月・四半期に上書きされる（ProjectPlan.tsxと同じ理由）。
      fitToRootPeriod: false,
    },
  },
})

// 進捗概況ページのtemplatePageUuid。ガント初期値を返すUIStateキーの
// 引き当てに使う（キーは表示状態 + templatePageUuid）。
export const DEMO_VERSION_REPORT_OVERVIEW_TEMPLATE_PAGE_UUID =
  templatePageUuidOf('overview')

// 工程別・チーム別ページのtemplatePageUuid。集計対象の初期値を返す
// UIStateキーの引き当てに使う（下のbuildDemoVersionReportGridDisplayState参照）。
export const DEMO_VERSION_REPORT_PROCESS_TEMPLATE_PAGE_UUID =
  templatePageUuidOf('process')

export const DEMO_VERSION_REPORT_GRID_TEMPLATE_PAGE_UUIDS = [
  DEMO_VERSION_REPORT_PROCESS_TEMPLATE_PAGE_UUID,
  templatePageUuidOf('team'),
]

// 行の高さ。本体の既定値（ROW_HEIGHT.MEDIUM = 50）より高くする。
// 目盛り上の値（下から4つ目）にしておくと、閲覧者がスライダーを触ったときにつまみが跳ねずに動く。
const DEMO_GRID_ROW_HEIGHT = 83

// 工程別ページの列の並びと表示/非表示。デモで見せたい順に左から固定する。
//
// pinnedも併せて持つ。AgGridは固定列を必ず非固定列より左に寄せるため、
// 並び順（applyOrder）だけ指定しても、固定状態が製品の既定のままだと
// ここで指定した順序にならない列が出る。「進捗率予実乖離」までを固定、
// 以降を非固定にして、指定した並びがそのまま成立するようにする。
//
// 予実乖離を固定側に入れるのは、遅れの大きさが横スクロールしても
// 常に見えるようにするため。固定列は見出しが水色（$BACKGROUND_COLOR_PINNED）
// になり、スクロールする集計値の列と視覚的に分かれる。
//
// 工程ステータスは出さない。進捗の良し悪しはステータス（天気）と
// 予実乖離で読む画面なので、WBS側のステータスは列を占めるわりに
// デモで語ることがない。
// drag列は持たない。lockPinned: true（columnDef.ts）でapplyColumnStateから
// 固定状態を変えられず、保存時も updateColumnStateConfig が colId==='drag' を
// 除いて書き戻すため、ここに置いても往復しない。
const DEMO_PROCESS_COLUMN_STATE = [
  { colId: 'rowNumber', pinned: 'left' },
  { colId: 'status', pinned: 'left' },
  { colId: 'name', pinned: 'left' },
  { colId: 'scheduledStartDate', pinned: 'left' },
  { colId: 'scheduledEndDate', pinned: 'left' },
  { colId: 'maxChildScheduledEndDate', pinned: 'left' },
  { colId: 'progressRateDiff', pinned: 'left' },
  { colId: 'scheduledProgressRate', pinned: null },
  { colId: 'progressRate', pinned: null },
  { colId: 'total', pinned: null },
  { colId: 'planned', pinned: null },
  { colId: 'completed', pinned: null },
  { colId: 'preceding', pinned: null },
  { colId: 'delay', pinned: null },
  { colId: 'remaining', pinned: null },
  { colId: 'unplanned', pinned: null },
  { colId: 'comment', pinned: null },
  { colId: 'wbsItemStatus', hide: true, pinned: null },
  { colId: 'team', hide: true, pinned: null },
]

// 工程別・チーム別ページの表示状態（集計トグル・行の高さ・列）の初期値。
// デモは初期表示を「タスクの個数」で揃える。
//
// 保存値が無いとき、本体は項目ごとに別々の既定値へフォールバックする
// （集計項目は工数、集計対象はグリッドが成果物・チーム別ポッパーがタスク）。
// その結果、初回表示に限って「総数38のセルをクリックすると明細が121件」の
// ように、同じセルなのに数が食い違って見える。
//
// 一度トグルを操作すれば値が保存されて解消するが、UIStateが空の初見の
// 閲覧者にだけ矛盾した数字が出るのはデモとして都合が悪い。そこで最初から
// 保存値がある状態にして、グリッドとポッパーの参照先を一致させる。
//
// 列の並びは工程別だけ指定する。チーム別は列構成が異なり（予定開始日・
// 予定終了日などを持たない）、同じ並びを渡しても対応する列がない。
export const buildDemoVersionReportGridDisplayState = (
  templatePageUuid: string
) => ({
  aggregateTarget: 'TASK',
  aggregateField: 'WBS_ITEM_COUNT',
  rowHeight: DEMO_GRID_ROW_HEIGHT,
  ...(templatePageUuid === DEMO_VERSION_REPORT_PROCESS_TEMPLATE_PAGE_UUID
    ? { columnState: DEMO_PROCESS_COLUMN_STATE }
    : {}),
})
